import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'

interface LotPayload {
  name?: string
  description?: string
  operation_id?: number | string
  area?: number
  price?: number
  is_private?: boolean
  lot_group_id?: number | string
  [key: string]: any
}

interface LotGroupPayload {
  name: string
  description?: string
  operation_id: number | string
  color?: string
  [key: string]: any
}

interface LotParams {
  operation?: number | string
  lot_group?: number | string
  is_private?: boolean
  page_size?: number
  [key: string]: any
}

/**
 * Creates a new lot
 * @param payload - Lot data
 * @returns API response
 */
export const createLot = (payload: LotPayload): Promise<ApiClientResult> => {
  return client
    .post(API_URL.LOTS.POST_LOT(), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a lot
 * @param lotId - Lot ID
 * @param payload - Lot data to update
 * @returns API response
 */
export const updateLot = (
  lotId: number | string,
  payload: Partial<LotPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.LOTS.PATCH_LOT(lotId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a lot
 * @param lotId - Lot ID
 * @returns API response
 */
export const deleteLot = (lotId: number | string): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.LOTS.DELETE_LOT(lotId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets lots with optional filtering
 * @param params - Query parameters
 * @returns API response with lots list
 */
export const getLots = (params: LotParams = {}): Promise<ApiClientResult> => {
  return client
    .get(API_URL.LOTS.GET_LOTS(), { params })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets a lot by ID
 * @param lotId - Lot ID
 * @returns API response with lot data
 */
export const getLot = (lotId: number | string): Promise<ApiClientResult> => {
  return client
    .get(API_URL.LOTS.GET_LOT(lotId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets lot groups with optional filtering
 * @param params - Query parameters
 * @returns API response with lot groups list
 */
export const getLotGroups = (params: LotParams): Promise<ApiClientResult> => {
  return client
    .get(API_URL.LOTS.GET_LOT_GROUPS(), { params })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Creates a new lot group
 * @param payload - Lot group data
 * @returns API response
 */
export const createLotsGroup = (
  payload: LotGroupPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.LOTS.POST_LOT_GROUP(), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a lot group
 * @param lotId - Lot group ID
 * @param payload - Lot group data to update
 * @returns API response
 */
export const updateLotsGroup = (
  lotId: number | string,
  payload: Partial<LotGroupPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.LOTS.PATCH_LOT_GROUP(lotId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a lot group
 * @param lotId - Lot group ID
 * @returns API response
 */
export const deleteLotGroup = (
  lotId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.LOTS.DELETE_LOT_GROUP(lotId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets private lots for an operation
 * @param id - Operation ID
 * @returns Array of private lots
 */
export const onGetPrivateLots = (id: number | string): Promise<any[]> => {
  return client
    .get(`/lots/?page_size=999&operation=${id}&is_private=true`)
    .then(response => response?.data?.results || [])
    .catch(error => {
      return error.response
    })
}
